import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { FiPlus, FiEye, FiEdit2, FiTrash2, FiDownload, FiCheck, FiX, FiSend } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { getPurchaseOrders, deletePurchaseOrder, acceptPurchaseOrder, updatePOStatus, downloadPOPDF, downloadPOExcel } from '../services/ClientService';
import { customSelect } from './Styles/CustomStyles';
import ConfirmModal from './ConfirmModal';
import { toast } from 'react-toastify';

// Utility function to recalculate summary with deduplication
const recalculatePOSummary = (products) => {
    const seenCommonInventoryIds = new Set();
    let totalProducts = 0;
    let totalMRP = 0;
    let totalRequestPrice = 0;

    products.forEach(product => {
        if (product.commonInventory) {
            const productIdStr = product.productId?.toString() || product.productId;
            if (!seenCommonInventoryIds.has(productIdStr)) {
                seenCommonInventoryIds.add(productIdStr);
                totalProducts++;
                totalMRP += product.totalMRP || (product.originalPrice * product.unitsRequired);
                totalRequestPrice += product.totalRequestPrice || (product.requestPrice * product.unitsRequired);
            }
        } else {
            totalProducts++;
            totalMRP += product.totalMRP || (product.originalPrice * product.unitsRequired);
            totalRequestPrice += product.totalRequestPrice || (product.requestPrice * product.unitsRequired);
        }
    });

    return {
        totalProducts,
        totalMRP: parseFloat(totalMRP.toFixed(2)),
        totalRequestPrice: parseFloat(totalRequestPrice.toFixed(2))
    };
};

const PurchaseOrders = () => {
    const navigate = useNavigate();
    const [purchaseOrders, setPurchaseOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [confirmModalVisible, setConfirmModalVisible] = useState(false);
    const [acceptModalVisible, setAcceptModalVisible] = useState(false);
    const [selectedPO, setSelectedPO] = useState(null);

    useEffect(() => {
        fetchPurchaseOrders();
    }, [statusFilter]);

    const fetchPurchaseOrders = async (search = '') => {
        setLoading(true);
        try {
            const filters = { searchTerm: search };
            if (statusFilter !== 'all') {
                filters.status = statusFilter;
            }
            const response = await getPurchaseOrders(filters);
            setPurchaseOrders(response.purchaseOrders || []);
        } catch (error) {
            console.error('Error fetching purchase orders:', error);
            toast.error('Failed to fetch purchase orders');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (value) => {
        setSearchTerm(value);
        const timer = setTimeout(() => {
            fetchPurchaseOrders(value);
        }, 500);
        return () => clearTimeout(timer);
    };

    const handleDeleteRequest = (po) => {
        setSelectedPO(po);
        setConfirmModalVisible(true);
    };

    const confirmDelete = async () => {
        setLoading(true);
        try {
            await deletePurchaseOrder(selectedPO._id);
            toast.success('Purchase order cancelled successfully');
            fetchPurchaseOrders(searchTerm);
        } catch (error) {
            console.error('Error deleting PO:', error);
            toast.error(error.response?.data?.error || 'Failed to cancel purchase order');
        } finally {
            setLoading(false);
            setConfirmModalVisible(false);
            setSelectedPO(null);
        }
    };

    const handleAcceptRequest = (po) => {
        setSelectedPO(po);
        setAcceptModalVisible(true);
    };

    const confirmAccept = async () => {
        setLoading(true);
        try {
            await acceptPurchaseOrder(selectedPO._id);
            toast.success('Purchase order accepted and stock distributed successfully!');
            fetchPurchaseOrders(searchTerm);
        } catch (error) {
            console.error('Error accepting PO:', error);
            toast.error(error.response?.data?.error || 'Failed to accept purchase order');
        } finally {
            setLoading(false);
            setAcceptModalVisible(false);
            setSelectedPO(null);
        }
    };

    const handleMarkAsSent = async (poId) => {
        setLoading(true);
        try {
            await updatePOStatus(poId, 'sent');
            toast.success('Purchase order marked as sent');
            fetchPurchaseOrders(searchTerm);
        } catch (error) {
            console.error('Error updating status:', error);
            toast.error('Failed to update status');
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadPDF = async (poId, poNumber) => {
        try {
            const blob = await downloadPOPDF(poId);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `PO_${poNumber}.pdf`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error('Error downloading PDF:', error);
            toast.error('Failed to download PDF');
        }
    };

    const handleDownloadExcel = async (poId, poNumber) => {
        try {
            const blob = await downloadPOExcel(poId);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `PO_${poNumber}.xlsx`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error('Error downloading Excel:', error);
            toast.error('Failed to download Excel');
        }
    };

    const getStatusBadge = (status, isAccepted) => {
        if (isAccepted) {
            return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">Accepted</span>;
        }
        switch (status) {
            case 'pending':
                return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">Pending</span>;
            case 'sent':
                return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">Sent</span>;
            case 'cancelled':
                return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">Cancelled</span>;
            default:
                return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800">{status}</span>;
        }
    };

    return (
        <div className="h-screen overflow-hidden bg-gradient-to-br from-indigo-50 via-white to-purple-50">
            {/* Header */}
            <div className="bg-white p-6 sticky top-0 z-10 shadow-md border-b border-gray-100">
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-4">
                    <h1 className="text-3xl font-bold text-gray-700">Purchase Orders</h1>
                    
                    <button
                        onClick={() => navigate('/create-purchase-order')}
                        className="bg-gradient-to-r from-green-500 to-green-700 hover:from-green-600 hover:to-green-800 text-white px-5 py-2.5 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
                    >
                        <FiPlus className="text-xl" />
                        Create Purchase Order
                    </button>
                </div>

                {/* Filters */}
                <div className="flex flex-col sm:flex-row gap-3">
                    <input
                        type="text"
                        placeholder="Search by PO number or vendor..."
                        value={searchTerm}
                        onChange={(e) => handleSearch(e.target.value)}
                        className="flex-1 px-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-transparent focus:shadow-[0_0_0_4px_rgba(139,92,246,0.2)] hover:border-gray-300 transition-all duration-300 bg-white"
                    />
                    
                    <div className="min-w-[200px]">
                        <Select
                            value={{ value: statusFilter, label: statusFilter === 'all' ? 'All Status' : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1) }}
                            onChange={(option) => setStatusFilter(option.value)}
                            options={[
                                { value: 'all', label: 'All Status' },
                                { value: 'pending', label: 'Pending' },
                                { value: 'sent', label: 'Sent' },
                                { value: 'accepted', label: 'Accepted' },
                                { value: 'cancelled', label: 'Cancelled' }
                            ]}
                            styles={customSelect}
                            className="react-select-container"
                            classNamePrefix="react-select"
                        />
                    </div>
                </div>
            </div>

            {/* PO List */}
            <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 180px)' }}>
                {loading ? (
                    <div className="flex justify-center items-center h-96">
                        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                    </div>
                ) : purchaseOrders.length === 0 ? (
                    <div className="text-center py-12">
                        <p className="text-gray-500 text-lg">No purchase orders found</p>
                        <button
                            onClick={() => navigate('/create-purchase-order')}
                            className="mt-4 text-blue-600 hover:text-blue-700 font-semibold"
                        >
                            Create your purchase order
                        </button>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {purchaseOrders.map((po) => {
                            // Recalculate summary for display
                            const recalculatedSummary = recalculatePOSummary(po.products || []);
                            
                            return (
                            <div
                                key={po._id}
                                className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow border border-gray-200 p-5"
                            >
                                <div className="flex flex-col lg:flex-row justify-between gap-4">
                                    {/* PO Info */}
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-2">
                                            <h3 className="text-lg font-bold text-gray-800">{po.poNumber}</h3>
                                            {getStatusBadge(po.status, po.isAccepted)}
                                        </div>
                                        
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                                            <div>
                                                <span className="text-gray-600 font-medium">Vendor:</span>
                                                <span className="ml-2 text-gray-800">{po.vendor?.vendorName}</span>
                                            </div>
                                            <div>
                                                <span className="text-gray-600 font-medium">Products:</span>
                                                <span className="ml-2 text-gray-800">{recalculatedSummary.totalProducts}</span>
                                            </div>
                                            <div>
                                                <span className="text-gray-600 font-medium">Original Total:</span>
                                                <span className="ml-2 text-gray-800">₹{recalculatedSummary.totalMRP.toFixed(2)}</span>
                                            </div>
                                            <div>
                                                <span className="text-gray-600 font-medium">Requested Total:</span>
                                                <span className="ml-2 text-green-600 font-semibold">₹{recalculatedSummary.totalRequestPrice.toFixed(2)}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Actions */}
                                    <div className="flex flex-wrap gap-2 items-start">
                                        {/* Download Buttons */}
                                        <button
                                            onClick={() => handleDownloadPDF(po._id, po.poNumber)}
                                            className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                                            title="Download PDF"
                                        >
                                            <FiDownload className="w-5 h-5" />
                                        </button>
                                        <button
                                            onClick={() => handleDownloadExcel(po._id, po.poNumber)}
                                            className="p-2 text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                                            title="Download Excel"
                                        >
                                            <FiDownload className="w-5 h-5" />
                                        </button>

                                        {!po.isAccepted && po.status !== 'cancelled' && (
                                            <>
                                                {po.status === 'pending' && (
                                                    <button
                                                        onClick={() => handleMarkAsSent(po._id)}
                                                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                        title="Mark as Sent"
                                                    >
                                                        <FiSend className="w-5 h-5" />
                                                    </button>
                                                )}
                                                
                                                <button
                                                    onClick={() => navigate(`/edit-purchase-order/${po._id}`)}
                                                    className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                                                    title="Edit"
                                                >
                                                    <FiEdit2 className="w-5 h-5" />
                                                </button>
                                                
                                                <button
                                                    onClick={() => handleDeleteRequest(po)}
                                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                    title="Cancel PO"
                                                >
                                                    <FiTrash2 className="w-5 h-5" />
                                                </button>
                                                
                                                <button
                                                    onClick={() => handleAcceptRequest(po)}
                                                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition-colors flex items-center gap-2"
                                                >
                                                    <FiCheck className="w-5 h-5" />
                                                    Accept & Receive Stock
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Delete Confirmation */}
            <ConfirmModal
                visible={confirmModalVisible}
                message="Are you sure you want to cancel this purchase order? This action cannot be undone."
                onConfirm={confirmDelete}
                onCancel={() => {
                    setConfirmModalVisible(false);
                    setSelectedPO(null);
                }}
                cancelButtonText="No, Keep It"
                confirmButtonText="Yes, Cancel PO"
            />

            {/* Accept Confirmation */}
            <ConfirmModal
                visible={acceptModalVisible}
                message="⚠️ This action is irreversible! Once accepted, you cannot edit this purchase order and inventory will be distributed to the warehouse. Do you want to continue?"
                onConfirm={confirmAccept}
                onCancel={() => {
                    setAcceptModalVisible(false);
                    setSelectedPO(null);
                }}
                cancelButtonText="Cancel"
                confirmButtonText="Yes, Accept PO"
            />
        </div>
    );
};

export default PurchaseOrders;
